import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Phone, Copy, Check, Loader2, AlertCircle, RefreshCw, Smartphone } from 'lucide-react';
import { sessionService } from '../../api/services';

// Indicatifs pays (équivalent léger à intl-tel-input, sans dépendance).
// dial = indicatif sans '+'. Liste volontairement focalisée (Europe + Afrique
// francophone + Amérique du Nord) ; extensible facilement.
const COUNTRIES = [
  { name: 'France', iso: 'FR', dial: '33', flag: '🇫🇷' },
  { name: 'Belgique', iso: 'BE', dial: '32', flag: '🇧🇪' },
  { name: 'Suisse', iso: 'CH', dial: '41', flag: '🇨🇭' },
  { name: 'Canada', iso: 'CA', dial: '1', flag: '🇨🇦' },
  { name: 'Cameroun', iso: 'CM', dial: '237', flag: '🇨🇲' },
  { name: "Côte d'Ivoire", iso: 'CI', dial: '225', flag: '🇨🇮' },
  { name: 'Sénégal', iso: 'SN', dial: '221', flag: '🇸🇳' },
  { name: 'Maroc', iso: 'MA', dial: '212', flag: '🇲🇦' },
  { name: 'Algérie', iso: 'DZ', dial: '213', flag: '🇩🇿' },
  { name: 'Tunisie', iso: 'TN', dial: '216', flag: '🇹🇳' },
  { name: 'Gabon', iso: 'GA', dial: '241', flag: '🇬🇦' },
  { name: 'Congo (RDC)', iso: 'CD', dial: '243', flag: '🇨🇩' },
  { name: 'Congo', iso: 'CG', dial: '242', flag: '🇨🇬' },
  { name: 'Bénin', iso: 'BJ', dial: '229', flag: '🇧🇯' },
  { name: 'Burkina Faso', iso: 'BF', dial: '226', flag: '🇧🇫' },
  { name: 'Mali', iso: 'ML', dial: '223', flag: '🇲🇱' },
  { name: 'Togo', iso: 'TG', dial: '228', flag: '🇹🇬' },
  { name: 'Guinée', iso: 'GN', dial: '224', flag: '🇬🇳' },
  { name: 'Madagascar', iso: 'MG', dial: '261', flag: '🇲🇬' },
  { name: 'Luxembourg', iso: 'LU', dial: '352', flag: '🇱🇺' },
  { name: 'Royaume-Uni', iso: 'GB', dial: '44', flag: '🇬🇧' },
  { name: 'Allemagne', iso: 'DE', dial: '49', flag: '🇩🇪' },
  { name: 'Espagne', iso: 'ES', dial: '34', flag: '🇪🇸' },
  { name: 'Italie', iso: 'IT', dial: '39', flag: '🇮🇹' },
  { name: 'Portugal', iso: 'PT', dial: '351', flag: '🇵🇹' },
  { name: 'Pays-Bas', iso: 'NL', dial: '31', flag: '🇳🇱' },
  { name: 'États-Unis', iso: 'US', dial: '1', flag: '🇺🇸' },
];

const POLL_INTERVAL_MS = 5000; // 12 req/min — marge sous la limite backend (évite 429)

// Déballe une réponse qu'elle soit brute ({status,...}) ou wrappée ({success,data}).
const unwrap = (body) =>
  body && body.success !== undefined && body.data !== undefined ? body.data : body;

// "ABCD1234" → "ABCD-1234" (2 blocs de 4) pour la lisibilité.
const formatCode = (code) => {
  if (!code) return '';
  const clean = String(code).trim();
  if (clean.length === 8) return `${clean.slice(0, 4)}-${clean.slice(4)}`;
  return clean;
};

const formatCountdown = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Connexion d'un compte WhatsApp par numéro de téléphone (code d'appairage).
 * @param {string} sessionLabel - libellé de session (ex: nom de l'agent)
 * @param {Function} onConnected - appelé quand la connexion réussit
 */
export default function WhatsAppPhoneConnect({ sessionLabel = 'Mon WhatsApp', onConnected }) {
  // idle | loading | waiting_code | code_ready | connected | expired | error
  const [phase, setPhase] = useState('idle');
  const [dial, setDial] = useState('33');
  const [nationalNumber, setNationalNumber] = useState('');
  const [code, setCode] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const pairingRef = useRef(null);
  const statusRef = useRef(null);
  const countdownRef = useRef(null);
  const mountedRef = useRef(true);
  const pairFailRef = useRef(0); // échecs consécutifs de pollPairing (tolérance 429)
  const connectedPhoneRef = useRef(null); // numéro complet saisi, transmis à onConnected

  const clearAllTimers = useCallback(() => {
    [pairingRef, statusRef, countdownRef].forEach((ref) => {
      if (ref.current) {
        clearInterval(ref.current);
        ref.current = null;
      }
    });
  }, []);

  // Nettoyage au démontage — évite les fuites de pollings.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  const handleConnected = useCallback(() => {
    if (!mountedRef.current) return;
    clearAllTimers();
    setPhase('connected');
    setTimeout(() => {
      if (mountedRef.current && onConnected) onConnected(connectedPhoneRef.current);
    }, 1200);
  }, [clearAllTimers, onConnected]);

  const startCountdown = useCallback((expiresIn) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setSecondsLeft(Math.max(0, parseInt(expiresIn, 10) || 0));
    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          if (mountedRef.current) {
            clearAllTimers();
            setPhase('expired');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearAllTimers]);

  // Polling du statut de connexion (relais WhatsFlow "tel quel" → plusieurs formes
  // possibles : { connected:true } / { status:'connected' } / { state:'connected' }).
  const pollStatus = useCallback(async (sessionId) => {
    try {
      const data = unwrap(await sessionService.getSessionStatus(sessionId)) || {};
      const isConnected =
        data.connected === true || data.status === 'connected' || data.state === 'connected';
      if (mountedRef.current && isConnected) handleConnected();
    } catch {
      // Erreur ponctuelle (incl. 429) ignorée — le polling continue.
    }
  }, [handleConnected]);

  // Polling du code d'appairage. Tolère les erreurs transitoires (incl. 429) :
  // on ne bascule en "error" qu'après plusieurs échecs consécutifs.
  const pollPairing = useCallback(async (sessionId) => {
    try {
      const data = unwrap(await sessionService.getPairingCode(sessionId));
      if (!mountedRef.current) return;
      pairFailRef.current = 0;

      if (data.status === 'connected') {
        handleConnected();
        return;
      }
      if (data.status === 'pairing_ready' && data.code) {
        setCode(data.code);
        setPhase('code_ready');
        startCountdown(data.expires_in);
        // Code obtenu : on arrête de poller le code et on bascule sur le statut
        // (un seul endpoint à la fois → ~20 req/min max, évite le 429).
        if (pairingRef.current) { clearInterval(pairingRef.current); pairingRef.current = null; }
        if (!statusRef.current) {
          pollStatus(sessionId);
          statusRef.current = setInterval(() => pollStatus(sessionId), POLL_INTERVAL_MS);
        }
      }
      // 'starting' → on garde le spinner et on continue
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err?.message || '';
      if (/expir/i.test(msg)) {
        clearAllTimers();
        setPhase('expired');
        return;
      }
      // 429 / erreur réseau ponctuelle → on ignore, on retentera au prochain tick.
      if (err?.status === 429) return;
      pairFailRef.current += 1;
      if (pairFailRef.current >= 4) {
        clearAllTimers();
        setErrorMsg(msg || 'Erreur lors de la récupération du code.');
        setPhase('error');
      }
    }
  }, [handleConnected, startCountdown, clearAllTimers, pollStatus]);

  const startConnection = useCallback(async () => {
    const national = nationalNumber.replace(/\D/g, '').replace(/^0+/, '');
    if (!national) {
      setErrorMsg('Veuillez saisir un numéro de téléphone.');
      setPhase('error');
      return;
    }
    const phone_number = `${dial}${national}`;
    connectedPhoneRef.current = phone_number;

    clearAllTimers();
    pairFailRef.current = 0;
    setErrorMsg('');
    setCode(null);
    setPhase('loading');

    try {
      const data = unwrap(
        await sessionService.connectByPhone({ session_label: sessionLabel, phone_number })
      );
      const sessionId = data.session_id;
      if (!sessionId) throw new Error("Aucun identifiant de session reçu.");
      if (!mountedRef.current) return;

      setPhase('waiting_code');

      // Un seul endpoint à la fois : on poll le CODE (toutes les 3 s). Le statut ne
      // sera pollé qu'une fois le code prêt → évite de doubler les requêtes (429).
      pollPairing(sessionId);
      pairingRef.current = setInterval(() => pollPairing(sessionId), POLL_INTERVAL_MS);
    } catch (err) {
      if (!mountedRef.current) return;
      const msg =
        err?.status === 429
          ? 'Trop de tentatives. Patientez quelques secondes puis réessayez.'
          : err?.message || 'Connexion impossible. Réessayez.';
      setErrorMsg(msg);
      setPhase('error');
    }
  }, [nationalNumber, dial, sessionLabel, clearAllTimers, pollPairing]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(code || '').replace(/\W/g, ''));
      setCopied(true);
      setTimeout(() => mountedRef.current && setCopied(false), 1800);
    } catch {
      /* clipboard indisponible — l'utilisateur peut recopier manuellement */
    }
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────

  if (phase === 'connected') {
    return (
      <div className="bg-green-500/10 border-2 border-green-500/30 rounded-lg p-6 text-center">
        <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
          <Check className="text-white" size={28} />
        </div>
        <h4 className="text-white font-semibold mb-1">WhatsApp connecté ✅</h4>
        <p className="text-text-gray text-sm">Votre compte est lié, redirection…</p>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-lg p-4 sm:p-6">
      {/* Formulaire numéro */}
      {(phase === 'idle' || phase === 'loading' || phase === 'error') && (
        <>
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Phone className="text-green-500" size={24} />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Connexion par numéro de téléphone</h4>
              <p className="text-text-gray text-sm">
                Recevez un code à saisir dans WhatsApp. Le numéro doit inclure son indicatif pays.
              </p>
            </div>
          </div>

          <label className="text-xs text-text-gray mb-1 block">Numéro WhatsApp</label>
          <div className="flex gap-2">
            <select
              value={dial}
              onChange={(e) => setDial(e.target.value)}
              disabled={phase === 'loading'}
              className="bg-primary-card text-white px-2 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green max-w-[130px]"
            >
              {COUNTRIES.map((c) => (
                <option key={c.iso} value={c.dial}>
                  {c.flag} +{c.dial}
                </option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="numeric"
              value={nationalNumber}
              onChange={(e) => setNationalNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && phase !== 'loading' && startConnection()}
              placeholder="6 90 00 00 00"
              disabled={phase === 'loading'}
              className="flex-1 min-w-0 bg-primary-card text-white px-4 py-3 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green"
            />
          </div>
          <p className="text-text-gray/70 text-xs mt-1">
            Numéro complet envoyé : <span className="text-text-gray">+{dial} {nationalNumber || '…'}</span>
          </p>

          {phase === 'error' && errorMsg && (
            <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
              <AlertCircle className="text-red-400 flex-shrink-0" size={16} />
              <p className="text-red-400 text-sm">{errorMsg}</p>
            </div>
          )}

          <button
            onClick={startConnection}
            disabled={phase === 'loading'}
            className="w-full mt-4 py-3 bg-accent-green text-black rounded-lg font-bold hover:bg-accent-green/80 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {phase === 'loading' ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Création de la session…
              </>
            ) : (
              <>
                <Smartphone size={18} />
                {phase === 'error' ? 'Réessayer' : 'Obtenir le code'}
              </>
            )}
          </button>
        </>
      )}

      {/* Attente du code */}
      {phase === 'waiting_code' && (
        <div className="text-center py-8">
          <Loader2 className="text-accent-green animate-spin mx-auto mb-4" size={40} />
          <p className="text-white font-semibold mb-1">Génération du code…</p>
          <p className="text-text-gray text-sm">Préparation de votre code d'appairage, un instant.</p>
        </div>
      )}

      {/* Code prêt */}
      {phase === 'code_ready' && (
        <div>
          <div className="text-center mb-5">
            <p className="text-text-gray text-sm mb-3">Votre code d'appairage</p>
            <div className="inline-flex items-center gap-3 bg-primary-card border-2 border-accent-green/40 rounded-xl px-6 py-4">
              <span className="text-3xl sm:text-4xl font-bold text-white tracking-[0.3em] font-mono select-all">
                {formatCode(code)}
              </span>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg bg-accent-green/15 text-accent-green hover:bg-accent-green/25 transition-all flex-shrink-0"
                title="Copier le code"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            <div className="mt-3 text-sm">
              {secondsLeft > 0 ? (
                <span className="text-text-gray">
                  Expire dans <span className="text-accent-green font-semibold font-mono">{formatCountdown(secondsLeft)}</span>
                </span>
              ) : (
                <span className="text-amber-400">Expiration imminente…</span>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-primary-card rounded-lg p-4">
            <h5 className="text-white font-semibold mb-3 text-sm">Comment saisir le code ?</h5>
            <ol className="space-y-2 text-sm text-text-gray">
              {[
                'Ouvrez WhatsApp sur votre téléphone',
                'Réglages → Appareils connectés → Lier un appareil',
                'Touchez « Lier avec un numéro de téléphone »',
                'Saisissez le code ci-dessus',
              ].map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-accent-green font-bold flex-shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex items-center justify-center gap-2 mt-4 text-text-gray text-xs">
            <Loader2 size={12} className="animate-spin" />
            En attente de la connexion…
          </div>
        </div>
      )}

      {/* Expiré */}
      {phase === 'expired' && (
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="text-amber-400" size={28} />
          </div>
          <h4 className="text-white font-semibold mb-1">Code expiré</h4>
          <p className="text-text-gray text-sm mb-4">Le code n'a pas été utilisé à temps. Générez-en un nouveau.</p>
          <button
            onClick={startConnection}
            className="px-6 py-3 bg-accent-green text-black rounded-lg font-bold hover:bg-accent-green/80 transition-all inline-flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Générer un nouveau code
          </button>
        </div>
      )}
    </div>
  );
}
